import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import Footer from "../components/Footer";

const Home = ({ user }) => {
  const [dailyImage, setDailyImage] = useState(null);
  const [loadingImage, setLoadingImage] = useState(true);
  const [showPrompt, setShowPrompt] = useState(false);
  const toastShown = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user && !toastShown.current) {
      toast.info("Please sign in to create AI images.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      toastShown.current = true;
    }
  }, [user]);

  useEffect(() => {
    const loadDailyImage = async () => {
      try {
        const snapshot = await getDocs(
          query(
            collection(db, "images"),
            orderBy("timestamp", "desc"),
            limit(100)
          )
        );
        const images = snapshot.docs
          .map((imageDoc) => ({ id: imageDoc.id, ...imageDoc.data() }))
          .filter((image) => image.imageUrl?.startsWith("http"));

        if (images.length === 0) return;

        const dayNumber = Math.floor(Date.now() / 86400000);
        setDailyImage(images[dayNumber % images.length]);
      } catch (error) {
        console.error("Unable to load the Portal of the Day:", error);
      } finally {
        setLoadingImage(false);
      }
    };

    loadDailyImage();
  }, []);

  return (
    <div className="relative min-h-screen">
      <main className="mx-auto max-w-5xl px-4 py-8">
        <section className="mb-8 text-center">
          <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">
            Imagine together
          </p>
          <h1 className="mb-3 text-4xl font-semibold text-slate-900">
            Welcome to Portl
          </h1>
          <p className="mx-auto max-w-xl text-slate-600">
            Create images from words, share the prompts behind them, and
            discover how other people imagine.
          </p>
        </section>

        <section className="mx-auto mb-8 max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <p className="mb-0 text-xs uppercase tracking-widest text-slate-500">
                From the archive
              </p>
              <h2 className="mb-0 text-2xl font-semibold text-slate-900">
                Portl of the Day
              </h2>
            </div>
            <span className="text-sm text-slate-500">
              {new Date().toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>

          {loadingImage && (
            <div className="flex min-h-96 items-center justify-center bg-slate-100 text-slate-500">
              Opening today&apos;s portl…
            </div>
          )}

          {!loadingImage && !dailyImage && (
            <div className="flex min-h-72 items-center justify-center bg-slate-100 px-6 text-center text-slate-500">
              No archive image is available yet.
            </div>
          )}

          {dailyImage && (
            <>
              <Link to={`/image/${dailyImage.id}`}>
                <img
                  src={dailyImage.imageUrl}
                  alt={dailyImage.prompt || "Portl of the Day"}
                  className="max-h-[650px] w-full bg-slate-100 object-contain"
                />
              </Link>
              <div className="p-5">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <p className="mb-0 text-sm text-slate-600">
                    Created by{" "}
                    <Link
                      to={`/profile/${dailyImage.userId}`}
                      className="font-semibold text-slate-900"
                    >
                      {dailyImage.displayName || "Anonymous User"}
                    </Link>
                  </p>
                  <Link
                    to={`/image/${dailyImage.id}`}
                    className="text-sm font-medium text-blue-700 no-underline"
                  >
                    View the post →
                  </Link>
                </div>

                {showPrompt ? (
                  <div className="rounded-xl bg-slate-100 p-4">
                    <p className="mb-1 text-xs uppercase tracking-wider text-slate-500">
                      The prompt
                    </p>
                    <p className="mb-0 text-slate-800">
                      {dailyImage.prompt || "No prompt was provided."}
                    </p>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 font-medium text-slate-800 hover:bg-slate-100"
                    onClick={() => setShowPrompt(true)}
                  >
                    Reveal the prompt
                  </button>
                )}
              </div>
            </>
          )}
        </section>

        <section className="mb-10 flex flex-wrap justify-center gap-3">
          <button
            className="rounded-xl bg-slate-900 px-6 py-3 font-medium text-white hover:bg-slate-700"
            onClick={() => navigate("/Dalle")}
          >
            Create an Image
          </button>
          <button
            className="rounded-xl border border-slate-300 bg-white px-6 py-3 font-medium text-slate-900 hover:bg-slate-100"
            onClick={() => navigate("/dalleimagery")}
          >
            Explore Portl
          </button>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Home;
